<template>
  <Teleport to="body">
    <Transition name="customer-creation-overlay">
      <div
        v-if="visible"
        class="customer-creation-modal-overlay customer-creation-modal-overlay--glass"
        @click.self="handleCancel"
      >
      <Transition name="customer-creation-panel" appear>
        <div
          v-if="visible"
          class="customer-creation-modal-card customer-creation-modal-card--glass"
          role="dialog"
          aria-labelledby="customer-creation-modal-title"
          title="Drag the bottom-right corner to resize the dialog"
          @click.stop
        >
      <div class="customer-creation-modal-header customer-creation-modal-header--glass">
        <h2 id="customer-creation-modal-title" class="customer-creation-modal-title">
          Create New Customer
        </h2>
        <button
          type="button"
          class="customer-creation-modal-close-btn"
          aria-label="Close"
          title="Close"
          @click="handleCancel"
        >
          <i class="bi bi-x-lg" aria-hidden="true"></i>
        </button>
      </div>

      <div class="customer-creation-modal-body customer-creation-modal-body--glass">
        <!-- Tab Navigation -->
        <TabNavigation
          v-model="activeTab"
          :tabs="tabs"
          @update:modelValue="handleTabChange"
        />

        <!-- Tab Content -->
        <div class="customer-creation-modal-tab-content customer-creation-modal-tab-content--glass">
          <!-- Basic Information Tab -->
          <div
            class="customer-creation-modal-tab-panel"
            :class="{ 'customer-creation-modal-tab-panel--active': activeTab === 'general' }"
            :aria-hidden="activeTab !== 'general'"
          >
          <div class="customer-creation-modal-section customer-creation-modal-section--glass">
              <!-- Row 1: Customer ID, Customer Name -->
            <div class="customer-creation-modal-form-row">
              <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-input">
                <LabelInputField
                  input-id="ccd-customer-id"
                  label="Customer ID"
                  v-model="form.customerId"
                  placeholder="Enter Customer ID"
                  required
                  flex
                  :has-error="customerIdExists || !!errors.customerId"
                  @input="handleCustomerIdInput"
                >
                  <div v-if="checkingCustomerId" class="customer-creation-modal-checking">Checking availability...</div>
                  <div v-if="customerIdExists" class="customer-creation-modal-error">This Customer ID already exists in this company.</div>
                  <div v-if="errors.customerId" class="customer-creation-modal-error">{{ errors.customerId }}</div>
                </LabelInputField>
              </div>
              <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-input">
                <LabelInputField
                  input-id="ccd-customer-name"
                  label="Customer Name"
                  v-model="form.customerName"
                  placeholder="Enter customer name"
                  required
                  flex
                  :has-error="!!errors.customerName"
                  @input="clearError('customerName')"
                >
                  <div v-if="errors.customerName" class="customer-creation-modal-error">{{ errors.customerName }}</div>
                </LabelInputField>
              </div>
              </div>

              <!-- Row 2: Active Checkbox -->
            <div class="customer-creation-modal-form-row">
              <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-checkbox">
                <LabelCheckboxField
                  input-id="ccd-active"
                  label="Active"
                  v-model="form.active"
                  flex
                />
              </div>
              <div class="customer-creation-modal-form-group">
                <!-- Empty space for alignment -->
              </div>
              </div>

              <!-- Row 3: Customer Since, Opening Balance -->
              <div class="customer-creation-modal-form-row">
                <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-date">
                  <LabelDateField
                    input-id="ccd-customer-since"
                    label="Customer Since"
                    v-model="form.customerSince"
                    flex
                    @change="handleDateChange"
                  />
                </div>
                <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-input">
                  <LabelInputField
                    input-id="ccd-opening-balance"
                    label="Opening Balance (USD)"
                    v-model="form.openingBalance"
                    type="number"
                    flex
                  />
                </div>
            </div>

              <!-- Row 4: Balance as of, Balance -->
            <div class="customer-creation-modal-form-row">
              <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-date">
                <LabelDateField
                  input-id="ccd-balance-as-of"
                  label="Balance as of"
                  v-model="form.balanceAsOf"
                  flex
                  @change="handleDateChange"
                />
              </div>
              <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-input">
                <LabelInputField
                  input-id="ccd-balance"
                  label="Balance (USD)"
                  v-model="form.balance"
                  type="number"
                  flex
                  disabled
                />
              </div>
              </div>

              <!-- Row 5: Fax, Website -->
              <div class="customer-creation-modal-form-row">
                <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-input">
                  <LabelInputField
                    input-id="ccd-fax"
                    label="Fax"
                    v-model="form.fax"
                    flex
                  />
                </div>
              <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-input">
                <LabelInputField
                  input-id="ccd-website"
                  label="Website"
                  v-model="form.website"
                  placeholder="Enter website (optional)"
                  flex
                />
                </div>
              </div>

              <!-- Notes - Full width -->
              <div class="customer-creation-modal-form-row">
                <div class="customer-creation-modal-form-group customer-creation-modal-full-width customer-creation-modal-form-group--label-description">
                  <LabelDescriptionField
                    input-id="ccd-notes"
                    label="Notes"
                    v-model="form.notes"
                    :rows="2"
                    flex
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Payment & Settings Tab -->
          <div
            class="customer-creation-modal-tab-panel"
            :class="{ 'customer-creation-modal-tab-panel--active': activeTab === 'details' }"
            :aria-hidden="activeTab !== 'details'"
          >
        <div class="customer-creation-modal-section customer-creation-modal-section--glass">
              <!-- Row 1: Sales Person, Payment Method -->
          <div class="customer-creation-modal-form-row">
            <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-dropdown">
              <LabelDropdown
                label="Sales Person"
                select-id="ccd-sales-person"
                v-model="form.salesPerson"
                flex
                wide
                searchable
                :options="salesPersonDropdownOptions"
                @change="handleSalesPersonChange"
              />
            </div>
            <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-dropdown">
              <LabelDropdown
                label="Payment Method"
                select-id="ccd-payment-method"
                v-model="form.paymentMethod"
                flex
                wide
                required
                :options="paymentMethodDropdownOptions"
                :has-error="!!errors.paymentMethod"
                @change="onPaymentMethodChange"
              >
                <div v-if="errors.paymentMethod" class="customer-creation-modal-error">{{ errors.paymentMethod }}</div>
              </LabelDropdown>
            </div>
          </div>

              <!-- Row 2: Payment Terms, Customer Group -->
              <div class="customer-creation-modal-form-row">
                <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-dropdown">
                  <LabelDropdown
                    :key="'payment-terms-' + paymentTerms.length"
                    label="Payment Terms"
                    select-id="ccd-payment-terms"
                    v-model="form.paymentTermId"
                    flex
                    wide
                    required
                    searchable
                    :options="paymentTermsDropdownOptions"
                    :has-error="!!errors.paymentTermId"
                    @change="handlePaymentTermChange"
                  >
                    <div v-if="errors.paymentTermId" class="customer-creation-modal-error">{{ errors.paymentTermId }}</div>
                  </LabelDropdown>
                </div>
                <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-dropdown">
                  <LabelDropdown
                    label="Customer Group"
                    select-id="ccd-customer-group"
                    v-model="form.customerGroup"
                    flex
                    wide
                    searchable
                    :options="customerGroupDropdownOptions"
                    @change="handleCustomerGroupChange"
                  />
                </div>
              </div>

              <!-- Row 2.5: Invoice Frequency -->
              <div class="customer-creation-modal-form-row">
                <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-dropdown">
                  <LabelDropdown
                    label="Invoice Frequency"
                    select-id="ccd-invoice-frequency"
                    v-model="form.invoiceFrequency"
                    flex
                    wide
                    required
                    searchable
                    :options="invoiceFrequencyDropdownOptions"
                    :has-error="!!errors.invoiceFrequency"
                    @change="handleInvoiceFrequencyChange"
                  >
                    <div v-if="errors.invoiceFrequency" class="customer-creation-modal-error">{{ errors.invoiceFrequency }}</div>
                  </LabelDropdown>
                </div>
                <div class="customer-creation-modal-form-group">
                  <!-- Empty space for alignment -->
                </div>
              </div>

              <!-- Row 3: Credit Limit, Bank Name -->
              <div class="customer-creation-modal-form-row">
                <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-input">
                  <LabelInputField
                    input-id="ccd-credit-limit"
                    label="Credit Limit (USD)"
                    v-model="form.creditLimit"
                    type="number"
                    flex
                  />
                </div>
                <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-input">
                  <LabelInputField
                    input-id="ccd-bank-name"
                    label="Bank Name"
                    v-model="form.bankName"
                    placeholder="Bank Name"
                    flex
                  />
                </div>
              </div>

              <!-- Row 4: Bank Account Number, Bank Branch -->
              <div class="customer-creation-modal-form-row">
                <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-input">
                  <LabelInputField
                    input-id="ccd-bank-account"
                    label="Bank Account Number"
                    v-model="form.bankAccountNumber"
                    placeholder="Bank Account Number"
                    flex
                  />
                </div>
                <div class="customer-creation-modal-form-group customer-creation-modal-form-group--label-input">
                  <LabelInputField
                    input-id="ccd-bank-branch"
                    label="Bank Branch"
                    v-model="form.bankBranch"
                    placeholder="Bank Branch"
                    flex
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Contacts Tab -->
          <div
            class="customer-creation-modal-tab-panel"
            :class="{ 'customer-creation-modal-tab-panel--active': activeTab === 'contacts' }"
            :aria-hidden="activeTab !== 'contacts'"
          >
            <div class="customer-creation-modal-contacts-section">
              <div class="customer-creation-modal-contacts-header">
                <h3>Contacts</h3>
                <div class="customer-creation-modal-add-contact-container">
                  <PrimaryButton
                    label="Add Contact"
                    icon="bi bi-plus-circle"
                    @click="addNewContactRow"
                  />
                  
                  <!-- Tooltip for when no contacts exist -->
                  <div v-if="contacts.length === 0 && newContacts.length === 0" class="customer-creation-modal-add-contact-tooltip">
                    <div class="customer-creation-modal-tooltip-content">
                      <div class="customer-creation-modal-tooltip-icon">👥</div>
                      <p>No contacts added yet</p>
                      <p class="customer-creation-modal-tooltip-subtitle">Contacts are optional. You can add them now or later when editing.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-if="errors.contacts" class="customer-creation-modal-error">{{ errors.contacts }}</div>
              
              <!-- Contact Cards -->
              <div class="customer-creation-modal-contacts-grid">
                <!-- Existing Contacts -->
                <div v-for="(contact, idx) in contacts" :key="'contact-' + idx" class="customer-creation-modal-contact-card">
                  <div class="customer-creation-modal-contact-card-header">
                    <LabelCheckboxField
                      :input-id="`ccd-contact-primary-${idx}`"
                      :label="contacts.length === 1 ? 'Primary Contact *' : 'Primary Contact'"
                      v-model="contact.primary"
                      custom-class="label-checkbox-field--card-compact"
                      :disabled="(contacts.length === 1) || (editingContactIdx !== idx && contact.primary && contacts.filter(c => c.primary).length === 1)"
                      @change="() => setPrimaryContact(idx)"
                    />
                  </div>
                  
                  <div class="customer-creation-modal-contact-fields">
                    <div class="customer-creation-modal-contact-field-row">
                      <div class="customer-creation-modal-contact-field customer-creation-modal-form-group--label-input">
                        <LabelInputField
                          :input-id="`ccd-contact-name-${idx}`"
                          label="Contact Name"
                          v-model="contact.name"
                          flex
                          :readonly="editingContactIdx !== idx"
                        />
                      </div>
                      <div class="customer-creation-modal-contact-field customer-creation-modal-form-group--label-input">
                        <LabelInputField
                          :input-id="`ccd-contact-title-${idx}`"
                          label="Title"
                          v-model="contact.title"
                          flex
                          :readonly="editingContactIdx !== idx"
                        />
                      </div>
                    </div>
                    
                    <div class="customer-creation-modal-contact-field-row">
                      <div class="customer-creation-modal-contact-field customer-creation-modal-form-group--label-input">
                        <LabelInputField
                          :input-id="`ccd-contact-phone-${idx}`"
                          label="Business Phone"
                          v-model="contact.phone"
                          flex
                          :readonly="editingContactIdx !== idx"
                        />
                      </div>
                      <div class="customer-creation-modal-contact-field customer-creation-modal-form-group--label-input">
                        <LabelInputField
                          :input-id="`ccd-contact-email-${idx}`"
                          label="Email"
                          v-model="contact.email"
                          type="email"
                          flex
                          :readonly="editingContactIdx !== idx"
                          :has-error="!!contactEmailErrors[`contact-${idx}`]"
                          @blur="validateContactEmail(contact.email, idx, false)"
                          @input="handleContactEmailInput(contact.email, idx, false)"
                        >
                          <div v-if="checkingContactEmails[`contact-${idx}`]" class="customer-creation-modal-checking">Checking availability...</div>
                          <div v-if="contactEmailErrors[`contact-${idx}`]" class="customer-creation-modal-error">{{ contactEmailErrors[`contact-${idx}`] }}</div>
                        </LabelInputField>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Edit and Delete buttons for existing contacts (when not editing) -->
                  <div v-if="editingContactIdx !== idx" class="customer-creation-modal-contact-save-actions">
                    <SecondaryButton
                      label="Edit"
                      icon=""
                      @click="editContact(idx)"
                    />
                    <SecondaryButton
                      label="Delete"
                      icon=""
                      custom-class="consultant-secondary-btn--danger"
                      @click="deleteContact(idx)"
                    />
                  </div>
                  
                  <!-- Save and Cancel buttons for editing existing contacts -->
                  <div v-if="editingContactIdx === idx" class="customer-creation-modal-contact-save-actions">
                    <SecondaryButton
                      label="Cancel"
                      icon="bi bi-x-lg"
                      @click="cancelEditContact"
                    />
                    <PrimaryButton
                      label="Update"
                      icon=""
                      compact
                      @click="updateContact()"
                    />
                  </div>
                </div>

                <!-- New Contact Cards -->
                <div v-for="(nc, nidx) in newContacts" :key="'new-contact-' + nidx" class="customer-creation-modal-contact-card customer-creation-modal-contact-card-new">
                  <div class="customer-creation-modal-contact-card-header">
                    <LabelCheckboxField
                      :input-id="`ccd-new-contact-primary-${nidx}`"
                      label="Primary Contact"
                      v-model="nc.primary"
                      custom-class="label-checkbox-field--card-compact"
                      :disabled="contacts.length + newContacts.length === 1"
                    />
                  </div>
                  
                  <div class="customer-creation-modal-contact-fields">
                    <div class="customer-creation-modal-contact-field-row">
                      <div class="customer-creation-modal-contact-field customer-creation-modal-form-group--label-input">
                        <LabelInputField
                          :input-id="`ccd-new-contact-name-${nidx}`"
                          label="Contact Name"
                          v-model="nc.name"
                          placeholder="Contact Name"
                          flex
                        />
                      </div>
                      <div class="customer-creation-modal-contact-field customer-creation-modal-form-group--label-input">
                        <LabelInputField
                          :input-id="`ccd-new-contact-title-${nidx}`"
                          label="Title"
                          v-model="nc.title"
                          placeholder="Title"
                          flex
                        />
                      </div>
                    </div>
                    
                    <div class="customer-creation-modal-contact-field-row">
                      <div class="customer-creation-modal-contact-field customer-creation-modal-form-group--label-input">
                        <LabelInputField
                          :input-id="`ccd-new-contact-phone-${nidx}`"
                          label="Business Phone"
                          v-model="nc.phone"
                          placeholder="Business Phone"
                          flex
                        />
                      </div>
                      <div class="customer-creation-modal-contact-field customer-creation-modal-form-group--label-input">
                        <LabelInputField
                          :input-id="`ccd-new-contact-email-${nidx}`"
                          label="Email"
                          v-model="nc.email"
                          type="email"
                          placeholder="Email"
                          flex
                          :has-error="!!contactEmailErrors[`new-contact-${nidx}`]"
                          @blur="validateContactEmail(nc.email, nidx, true)"
                          @input="handleContactEmailInput(nc.email, nidx, true)"
                        >
                          <div v-if="checkingContactEmails[`new-contact-${nidx}`]" class="customer-creation-modal-checking">Checking availability...</div>
                          <div v-if="contactEmailErrors[`new-contact-${nidx}`]" class="customer-creation-modal-error">{{ contactEmailErrors[`new-contact-${nidx}`] }}</div>
                        </LabelInputField>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Save and Cancel buttons for new contacts -->
                  <div class="customer-creation-modal-contact-save-actions">
                    <SecondaryButton
                      label="Cancel"
                      icon="bi bi-x-lg"
                      @click="cancelNewContact(nidx)"
                    />
                    <PrimaryButton
                      label="Save"
                      icon=""
                      compact
                      @click="saveNewContact(nidx)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Addresses Tab -->
          <div
            class="customer-creation-modal-tab-panel"
            :class="{ 'customer-creation-modal-tab-panel--active': activeTab === 'addresses' }"
            :aria-hidden="activeTab !== 'addresses'"
          >
            <div class="customer-creation-modal-addresses-section">
              <div class="customer-creation-modal-addresses-header">
                <h3>Addresses</h3>
                <div class="customer-creation-modal-add-address-container">
                  <PrimaryButton
                    label="Add Address"
                    icon="bi bi-plus-circle"
                    @click="addNewAddressRow"
                  />
                  
                  <!-- Tooltip for when no addresses exist -->
                  <div v-if="addresses.length === 0 && newAddresses.length === 0" class="customer-creation-modal-add-address-tooltip">
                    <div class="customer-creation-modal-tooltip-content">
                      <div class="customer-creation-modal-tooltip-icon">📍</div>
                      <p>No addresses added yet</p>
                      <p class="customer-creation-modal-tooltip-subtitle">Addresses are optional. You can add them now or later when editing.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-if="errors.addresses" class="customer-creation-modal-error">{{ errors.addresses }}</div>
              
              <!-- Address Cards -->
              <div class="customer-creation-modal-addresses-grid">
                <!-- Existing Addresses -->
                <div v-for="(address, idx) in addresses" :key="'address-' + idx" class="customer-creation-modal-address-card">
                  <CustomerAddressFields
                    :model-value="address"
                    :id-prefix="`ccd-address-${idx}`"
                    :disabled="editingAddressIdx !== idx"
                    :type-options="addressTypeDropdownOptions(idx, false)"
                    @update:modelValue="addresses[idx] = $event"
                  />
                  
                  <!-- Edit and Delete buttons for existing addresses (when not editing) -->
                  <div v-if="editingAddressIdx !== idx" class="customer-creation-modal-address-save-actions">
                    <SecondaryButton
                      label="Edit"
                      icon=""
                      @click="editAddress(idx)"
                    />
                    <SecondaryButton
                      label="Delete"
                      icon=""
                      custom-class="consultant-secondary-btn--danger"
                      @click="deleteAddress(idx)"
                    />
                  </div>
                  
                  <!-- Save and Cancel buttons for editing existing addresses -->
                  <div v-if="editingAddressIdx === idx" class="customer-creation-modal-address-save-actions">
                    <SecondaryButton
                      label="Cancel"
                      icon="bi bi-x-lg"
                      @click="cancelEditAddress"
                    />
                    <PrimaryButton
                      label="Update"
                      icon=""
                      compact
                      @click="updateAddress(idx)"
                    />
                  </div>
                </div>

                <!-- New Address Cards -->
                <div v-for="(na, nidx) in newAddresses" :key="'new-address-' + nidx" class="customer-creation-modal-address-card customer-creation-modal-address-card-new">
                  <CustomerAddressFields
                    :model-value="na"
                    :id-prefix="`ccd-new-address-${nidx}`"
                    :disabled="false"
                    :type-options="addressTypeDropdownOptions(nidx, true)"
                    @update:modelValue="newAddresses[nidx] = $event"
                  />
                  
                  <!-- Save and Cancel buttons for new addresses -->
                  <div class="customer-creation-modal-address-save-actions">
                    <SecondaryButton
                      label="Cancel"
                      icon="bi bi-x-lg"
                      @click="cancelNewAddress(nidx)"
                    />
                    <PrimaryButton
                      label="Save"
                      icon=""
                      compact
                      @click="saveNewAddress(nidx)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Documents Tab -->
          <div
            class="customer-creation-modal-tab-panel customer-creation-modal-tab-panel--documents"
            :class="{ 'customer-creation-modal-tab-panel--active': activeTab === 'documents' }"
            :aria-hidden="activeTab !== 'documents'"
          >
            <div class="customer-creation-modal-documents-section">
              <div class="customer-creation-modal-documents-header">
                <h3>Documents</h3>
                <div class="customer-creation-modal-add-address-container">
                  <PrimaryButton
                    label="Add Document"
                    icon="bi bi-plus-circle"
                    @click="$refs.addDocumentFileInput.click()"
                  />
                  <input
                    id="add-customer-document-input"
                    type="file"
                    ref="addDocumentFileInput"
                    class="customer-creation-modal-document-file-input"
                    @change="handleDocumentFileUpload"
                    multiple
                  />
                </div>
              </div>
              
              <!-- Split View: Documents List (Left) and Preview (Right) -->
              <div class="customer-creation-modal-documents-split-view">
                <!-- Left Side: Documents List -->
                <div class="customer-creation-modal-documents-list-container">
                  <div v-if="documents.length > 0" class="customer-creation-modal-documents-list">
                    <div 
                      v-for="(document, index) in documents" 
                      :key="index" 
                      class="customer-creation-modal-document-item"
                      :class="{ 'active': selectedDocumentIndex === index }"
                      @click="selectDocument(index)"
                    >
                      <div class="customer-creation-modal-document-info">
                        <div class="customer-creation-modal-document-name">
                          <i class="bi bi-file-earmark"></i> {{ document.fileName }}
                        </div>
                        <div class="customer-creation-modal-document-meta">
                          <span class="customer-creation-modal-document-size">{{ formatFileSize(document.fileSize) }}</span>
                          <span class="customer-creation-modal-document-date">{{ formatDate(document.uploadedAt) }}</span>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        @click.stop="removeCustomerDocument(index)" 
                        class="customer-creation-modal-document-delete-btn" 
                        title="Remove document"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                  <div v-else class="customer-creation-modal-no-documents-message">
                    <div class="customer-creation-modal-no-documents-icon">📄</div>
                    <p>No documents added yet</p>
                    <p class="customer-creation-modal-tooltip-subtitle">Click "Add Document" to upload files</p>
                  </div>
                </div>
                
                <!-- Right Side: Preview -->
                <div class="customer-creation-modal-document-preview-container">
                  <div v-if="selectedDocumentIndex !== null && documents[selectedDocumentIndex]" class="customer-creation-modal-document-preview">
                    <div class="customer-creation-modal-document-preview-header">
                      <h4>{{ documents[selectedDocumentIndex].fileName }}</h4>
                    </div>
                    <div class="customer-creation-modal-document-preview-content">
                      <div v-if="loadingPreview" class="customer-creation-modal-document-preview-loading">
                        <div class="customer-creation-modal-document-preview-icon">
                          <i class="bi bi-arrow-repeat" style="font-size: 2rem; color: #0099cc; animation: spin 1s linear infinite;"></i>
                        </div>
                        <p>Loading preview...</p>
                      </div>
                      <div v-else-if="currentPreviewUrl && isImageFile(documents[selectedDocumentIndex])" class="customer-creation-modal-document-preview-image">
                        <img :src="currentPreviewUrl" :alt="documents[selectedDocumentIndex].fileName" @error="handlePreviewError" />
                      </div>
                      <div v-else-if="currentPreviewUrl && isPdfFile(documents[selectedDocumentIndex])" class="customer-creation-modal-document-preview-pdf">
                        <iframe :src="currentPreviewUrl" frameborder="0" @error="handlePreviewError"></iframe>
                      </div>
                      <div v-else-if="previewError" class="customer-creation-modal-document-preview-error">
                        <div class="customer-creation-modal-document-preview-icon">
                          <i class="bi bi-exclamation-triangle" style="font-size: 4rem; color: #dc3545;"></i>
                        </div>
                        <p>Failed to load preview</p>
                        <PrimaryButton
                          label="Open in New Window"
                          icon="bi bi-box-arrow-up-right"
                          @click="openDocumentInNewWindow(documents[selectedDocumentIndex])"
                        />
                      </div>
                      <div v-else class="customer-creation-modal-document-preview-unsupported">
                        <div class="customer-creation-modal-document-preview-icon">
                          <i class="bi bi-file-earmark" style="font-size: 4rem; color: #ccc;"></i>
                        </div>
                        <p>Preview not available for this file type</p>
                        <PrimaryButton
                          label="Open in New Window"
                          icon="bi bi-box-arrow-up-right"
                          @click="openDocumentInNewWindow(documents[selectedDocumentIndex])"
                        />
                      </div>
                    </div>
                  </div>
                  <div v-else class="customer-creation-modal-document-preview-empty">
                    <div class="customer-creation-modal-document-preview-icon">
                      <i class="bi bi-file-earmark-text" style="font-size: 4rem; color: #ccc;"></i>
                    </div>
                    <p>Select a document to preview</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="customer-creation-modal-actions customer-creation-modal-actions--glass">
        <!-- Next button for first four tabs -->
        <PrimaryButton
          v-if="activeTab !== 'documents'"
          label="Next"
          icon=""
          :disabled="hasCurrentTabErrors"
          @click="handleNext"
        />
        
        <!-- Save button only on last tab -->
        <PrimaryButton
          v-if="activeTab === 'documents'"
          :label="loading ? 'Creating...' : 'Save'"
          icon=""
          :loading="loading"
          :disabled="loading"
          @click="handleOk"
        />
        <SecondaryButton
          label="Cancel"
          icon="bi bi-x-lg"
          @click="handleCancel"
        />
      </div>
        </div>
      </Transition>
      </div>
    </Transition>
  </Teleport>

  <AddNewPaymentTerm
    :visible="showAddPaymentTermModal"
    :companyId="companyId"
    @ok="onAddPaymentTermOk"
    @cancel="onAddPaymentTermCancel"
  />

  <AddNewCustomerGroup
    :visible="showAddCustomerGroupModal"
    :companyId="companyId"
    @ok="onAddCustomerGroupOk"
    @cancel="onAddCustomerGroupCancel"
  />

  <AddNewInvoiceFrequency
    :visible="showAddInvoiceFrequencyModal"
    :companyId="companyId"
    @ok="onAddInvoiceFrequencyOk"
    @cancel="onAddInvoiceFrequencyCancel"
  />

  <SalesPersonCreationDialog
    :visible="showAddSalesPersonModal"
    :companyId="companyId"
    @ok="onAddSalesPersonOk"
    @cancel="onAddSalesPersonCancel"
    @close="onAddSalesPersonCancel"
  />

  <ErrorModal
    :show="showErrorModal"
    title="Validation Error"
    :message="errorMessage"
    @close="closeErrorModal"
  />

  <SuccessModal
    :show="showSuccessModal"
    title="Success"
    :message="successMessage"
    button-text="Yes"
    secondary-button-text="No"
    @close="stayOnPageAfterSave"
    @secondary="leavePageAfterSave"
  />
</template>

<script>
import '../styles/CustomerCreationDialog.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';
import customerService from '@/services/customer.service.js';
import CompanyService from '@/services/company.service.js';
import AddNewPaymentTerm from '@/modules/company/views/AddNewPaymentTerm.vue';
import AddNewCustomerGroup from '@/modules/company/views/AddNewCustomerGroup.vue';
import AddNewInvoiceFrequency from '@/modules/company/views/AddNewInvoiceFrequency.vue';
import SalesPersonCreationDialog from '@/modules/company/views/SalesPersonCreationDialog.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import TabNavigation from '@/components/TabNavigation.vue';
import CustomerAddressFields from '@/modules/customer/components/CustomerAddressFields.vue';
import LabelInputFieldComponent from '@/modules/consultant/components/LabelInputField.vue';
import LabelCheckboxFieldComponent from '@/modules/consultant/components/LabelCheckboxField.vue';
import LabelDescriptionFieldComponent from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelDropdownComponent from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateFieldComponent from '@/modules/consultant/components/LabelDateField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

const customerCreationDialogComponents = {
  AddNewPaymentTerm,
  AddNewCustomerGroup,
  AddNewInvoiceFrequency,
  SalesPersonCreationDialog,
  ErrorModal,
  SuccessModal,
  TabNavigation,
  CustomerAddressFields,
  LabelInputField: LabelInputFieldComponent,
  LabelCheckboxField: LabelCheckboxFieldComponent,
  LabelDescriptionField: LabelDescriptionFieldComponent,
  LabelDropdown: LabelDropdownComponent,
  LabelDateField: LabelDateFieldComponent,
  PrimaryButton,
  SecondaryButton,
};

export default {
  name: 'CustomerCreationDialog',
  components: customerCreationDialogComponents,
  emits: ['close', 'ok', 'cancel'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: [String, Number],
      required: true
    },
    initialCustomerName: {
      type: String,
      default: ''
    },
    initialCustomerId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      activeTab: 'general',
      tabs: [
        { id: 'general', name: 'Basic Information' },
        { id: 'details', name: 'Payment & Settings' },
        { id: 'contacts', name: 'Contact Details' },
        { id: 'addresses', name: 'Address Information' },
        { id: 'documents', name: 'Documents' }
      ],
      form: {
        customerId: '',
        customerName: '',
        active: true,
        customerSince: this.getCurrentDate(),
        openingBalance: 0,
        balanceAsOf: this.getCurrentDate(),
        balance: 0,
        fax: '',
        website: '',
        notes: '',
        salesPerson: '',
        paymentMethod: '',
        paymentTermId: '',
        customerGroup: '',
        creditLimit: 0,
        bankName: '',
        bankAccountNumber: '',
        bankBranch: '',
        email: '',
        phone: '',
        invoiceFrequency: ''
      },
      contacts: [],
      newContacts: [],
      editingContactIdx: null,
      addresses: [],
      newAddresses: [],
      editingAddressIdx: null,
      paymentTerms: [],
      salesPersons: [],
      customerGroups: [],
      invoiceFrequencies: [],
      showAddPaymentTermModal: false,
      showAddCustomerGroupModal: false,
      showAddInvoiceFrequencyModal: false,
      showAddSalesPersonModal: false,
      previousPaymentTerm: '',
      previousCustomerGroup: '',
      previousSalesPerson: '',
      previousInvoiceFrequency: '',
      errors: {},
      loading: false,
      showErrorModal: false,
      errorMessage: '',
      showSuccessModal: false,
      successMessage: '',
      createdCustomer: null,
      customerIdExists: false,
      checkingCustomerId: false,
      customerIdCheckTimeout: null,
      contactEmailErrors: {},
      checkingContactEmails: {},
      contactEmailCheckTimeouts: {},
      documents: [],
      selectedDocumentIndex: null,
      documentPreviewUrls: {}, // Cache for preview URLs
      currentPreviewUrl: '', // Current preview URL for selected document
      loadingPreview: false,
      previewError: false,
    };
  },
  methods: {
    resolveSelectValue(eventOrValue) {
      if (eventOrValue != null && typeof eventOrValue === 'object' && 'target' in eventOrValue) {
        return eventOrValue.target.value;
      }
      return eventOrValue;
    },
    addressTypeDropdownOptions(idx, isNew) {
      const types = this.getAvailableAddressTypes(idx, isNew);
      return [
        { value: '', label: 'Select Type' },
        ...types.map((type) => ({ value: type, label: type })),
      ];
    },
    onPaymentMethodChange(eventOrValue) {
      this.form.paymentMethod = this.resolveSelectValue(eventOrValue);
      this.clearError('paymentMethod');
    },
    getCurrentDate() {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      return `${month}/${day}/${year}`;
    },

    setInitialCustomerData() {
      if (this.initialCustomerName && this.initialCustomerName.trim() && this.visible) {
        const customerName = this.initialCustomerName.trim();
        // Set customer name immediately
        this.form.customerName = customerName;
        
        // Format customer ID: Customer Name-Orig ID
        if (this.initialCustomerId && this.initialCustomerId.trim()) {
          // Combine customer name with Orig ID: "Customer Name-Orig ID"
          const origId = this.initialCustomerId.trim();
          // Clean customer name for ID (remove special characters, keep spaces as hyphens)
          const cleanName = customerName.replace(/[^a-zA-Z0-9\s]/g, '').trim().replace(/\s+/g, '-');
          this.form.customerId = `${cleanName}-${origId}`;
        } else {
          // Auto-generate customer ID from customer name if Orig ID not available
          const generatedId = this.generateCustomerIdFromName(customerName);
          if (generatedId) {
            this.form.customerId = generatedId;
          }
        }
        
        // Clear any existing errors for these fields
        this.clearError('customerName');
        this.clearError('customerId');
      }
    },

    generateCustomerIdFromName(customerName) {
      if (!customerName || !customerName.trim()) {
        return '';
      }
      
      // Remove extra spaces and trim
      const name = customerName.trim();
      
      // Extract first word or first 4 characters, remove special characters
      const words = name.split(/\s+/);
      let baseId = '';
      
      if (words.length > 0) {
        // Take first word, remove special characters, take first 4 chars
        baseId = words[0].replace(/[^a-zA-Z0-9]/g, '').substring(0, 4).toUpperCase();
      }
      
      // If first word is too short, use first 4 characters of the full name
      if (baseId.length < 3) {
        baseId = name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 4).toUpperCase();
      }
      
      // Add a short timestamp suffix to ensure uniqueness (last 4 digits of timestamp)
      const timestamp = Date.now().toString().slice(-4);
      
      return `${baseId}-${timestamp}`;
    },
    
    handleTabChange(newTab) {
      if (newTab === 'contacts') {
        this.initializeDefaultContacts();
      } else if (newTab === 'addresses') {
        this.initializeDefaultAddresses();
      }
    },

    initializeDefaultContacts() {
      // If no contacts exist, add one default contact row
      if (this.contacts.length === 0 && this.newContacts.length === 0) {
        this.newContacts.push({ name: '', title: '', phone: '', email: '', primary: true });
      }
    },

    initializeDefaultAddresses() {
      // If no addresses exist, add one default address row with Billing type, US as default country, and Utah as default state
      if (this.addresses.length === 0 && this.newAddresses.length === 0) {
        this.newAddresses.push({ type: 'Billing', address1: '', address2: '', city: '', state: 'UT', postalCode: '', country: 'US' });
      }
    },

    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field];
      }
    },

    async handleCustomerIdInput() {
      this.clearError('customerId');
      this.customerIdExists = false;
      
      // Only check if customer ID is not empty and company ID exists
      if (!this.form.customerId || !this.form.customerId.trim()) {
        return;
      }
      
      if (!this.companyId) {
        return;
      }
      
      // Debounce the API call
      clearTimeout(this.customerIdCheckTimeout);
      this.checkingCustomerId = true;
      
      this.customerIdCheckTimeout = setTimeout(async () => {
        try {
          const response = await customerService.checkCustomerIdExists(this.form.customerId.trim(), this.companyId);
          this.customerIdExists = response.data.exists;
        } catch (error) {
          logger.error('Error checking customer ID:', error);
          // Don't show error to user for validation check failures
        } finally {
          this.checkingCustomerId = false;
        }
      }, 500); // 500ms debounce
    },

    resetForm() {
      this.form = {
        customerId: '',
        customerName: '',
        active: true,
        customerSince: this.getCurrentDate(),
        openingBalance: 0,
        balanceAsOf: this.getCurrentDate(),
        balance: 0,
        fax: '',
        website: '',
        notes: '',
        salesPerson: '',
        paymentMethod: '',
        paymentTermId: '',
        customerGroup: '',
        creditLimit: 0,
        bankName: '',
        bankAccountNumber: '',
        bankBranch: '',
        email: '',
        phone: '',
        invoiceFrequency: ''
      };
      this.contacts = [];
      this.newContacts = [];
      this.addresses = [];
      this.newAddresses = [];
      this.editingContactIdx = null;
      this.editingAddressIdx = null;
      this.errors = {};
      this.loading = false;
      this.showErrorModal = false;
      this.errorMessage = '';
      this.showSuccessModal = false;
      this.successMessage = '';
      this.createdCustomer = null;
      this.activeTab = 'general';
      this.customerIdExists = false;
      this.checkingCustomerId = false;
      clearTimeout(this.customerIdCheckTimeout);
      this.contactEmailErrors = {};
      this.checkingContactEmails = {};
      Object.keys(this.contactEmailCheckTimeouts).forEach(key => {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
      });
      this.contactEmailCheckTimeouts = {};
      // Reset documents
      this.documents = [];
      this.selectedDocumentIndex = null;
      this.documentPreviewUrls = {};
      this.currentPreviewUrl = '';
      this.loadingPreview = false;
      this.previewError = false;
    },

    handleDateChange() {
      // This method is called when a date is selected from the DatePicker
      // The value is already in MM/DD/YYYY format and automatically bound to the form
    },

    validateDate(field) {
      const value = this.form[field];
      if (!value) return true;
      
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
      if (!dateRegex.test(value)) return false;
      
      const parts = value.split('/');
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      
      const date = new Date(year, month - 1, day);
      return date.getFullYear() === year && 
             date.getMonth() === month - 1 && 
             date.getDate() === day;
    },

    convertDateToISO(dateString) {
      if (!dateString) return null;
      
      const parts = dateString.split('/');
      if (parts.length !== 3) return null;
      
      const month = parts[0];
      const day = parts[1];
      const year = parts[2];
      
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    },

    resolveContactsForSubmit() {
      const rows = [];

      this.contacts.forEach((c) => {
        rows.push({
          name: String(c.name || '').trim(),
          title: String(c.title || '').trim(),
          phone: String(c.phone || '').trim(),
          email: String(c.email || '').trim(),
          primary: !!c.primary
        });
      });

      for (const nc of this.newContacts) {
        const name = String(nc.name || '').trim();
        const title = String(nc.title || '').trim();
        const phone = String(nc.phone || '').trim();
        const email = String(nc.email || '').trim();
        const anyFilled = name || title || phone || email;
        if (!anyFilled) {
          continue;
        }
        if (!name || !title || !phone || !email) {
          return {
            ok: false,
            error: 'Each contact row that has any field filled must include Contact Name, Title, Business Phone, and Email.',
            contacts: []
          };
        }
        rows.push({
          name,
          title,
          phone,
          email,
          primary: !!nc.primary
        });
      }

      if (rows.length === 0) {
        return { ok: true, contacts: [] };
      }

      if (rows.length === 1) {
        rows[0].primary = true;
        return { ok: true, contacts: rows };
      }

      const hasPrimary = rows.some((r) => r.primary);
      if (!hasPrimary) {
        return { ok: false, error: 'At least one contact must be marked as primary.', contacts: [] };
      }

      return { ok: true, contacts: rows };
    },

    validateCurrentTab() {
      let hasErrors = false;
      
      if (this.activeTab === 'general') {
        // Validate Basic Information tab
        if (!this.form.customerId || this.form.customerId.trim() === '') {
          this.errors.customerId = 'Customer ID is required.';
          hasErrors = true;
        }
        if (this.customerIdExists) {
          this.errors.customerId = 'This Customer ID already exists in this company.';
          hasErrors = true;
        }
        if (!this.form.customerName || this.form.customerName.trim() === '') {
          this.errors.customerName = 'Customer Name is required.';
          hasErrors = true;
        }
        
        if (this.form.customerSince && !this.validateDate('customerSince')) {
          this.errors.customerSince = 'Please enter a valid date in MM/DD/YYYY format for Customer Since';
          hasErrors = true;
        }
        if (this.form.balanceAsOf && !this.validateDate('balanceAsOf')) {
          this.errors.balanceAsOf = 'Please enter a valid date in MM/DD/YYYY format for Balance as of';
          hasErrors = true;
        }
      }
      
      if (this.activeTab === 'details') {
        // Validate Payment & Settings tab
        if (!this.form.paymentMethod) {
          this.errors.paymentMethod = 'Payment Method is required.';
          hasErrors = true;
        }
        if (!this.form.paymentTermId) {
          this.errors.paymentTermId = 'Payment Terms is required.';
          hasErrors = true;
        }
        if (!this.form.invoiceFrequency) {
          this.errors.invoiceFrequency = 'Invoice Frequency is required.';
          hasErrors = true;
        }
      }
      
      if (this.activeTab === 'contacts') {
        const contactsResolution = this.resolveContactsForSubmit();
        if (!contactsResolution.ok) {
          this.errors.contacts = contactsResolution.error;
          hasErrors = true;
        }
      }
      
      if (this.activeTab === 'addresses') {
        // Addresses are optional on create
      }
      
      return hasErrors;
    },

    handleNext() {
      // Clear previous errors
      this.errors = {};
      
      // Validate only current tab
      const hasErrors = this.validateCurrentTab();
      
      if (hasErrors) {
        // Show error modal with validation errors
        const errorMessages = Object.values(this.errors)
          .map(message => `• ${message}`)
          .join('\n');
        this.errorMessage = errorMessages;
        this.showErrorModal = true;
        return;
      }

      // If no errors in current tab, navigate to next tab
      this.navigateToNextTab();
    },

    navigateToNextTab() {
      const tabs = ['general', 'details', 'contacts', 'addresses', 'documents'];
      const currentIndex = tabs.indexOf(this.activeTab);
      
      if (currentIndex < tabs.length - 1) {
        this.activeTab = tabs[currentIndex + 1];
      }
    },

    validate() {
      const errors = {};
      
      // Validate customer ID
      if (!this.form.customerId || this.form.customerId.trim() === '') {
        errors.customerId = 'Customer ID is required.';
      }
      if (this.customerIdExists) {
        errors.customerId = 'This Customer ID already exists in this company.';
      }
      
      // Validate customer name
      if (!this.form.customerName || this.form.customerName.trim() === '') {
        errors.customerName = 'Customer Name is required.';
      }
      
      // Validate dates
      if (this.form.customerSince && !this.validateDate('customerSince')) {
        errors.customerSince = 'Please enter a valid date in MM/DD/YYYY format for Customer Since';
      }
      if (this.form.balanceAsOf && !this.validateDate('balanceAsOf')) {
        errors.balanceAsOf = 'Please enter a valid date in MM/DD/YYYY format for Balance as of';
      }

      const contactsResolution = this.resolveContactsForSubmit();
      if (!contactsResolution.ok) {
        errors.contacts = contactsResolution.error;
      }

      // Validate Payment Method and Payment Terms
      if (!this.form.paymentMethod) {
        errors.paymentMethod = 'Payment Method is required.';
      }
      if (!this.form.paymentTermId) {
        errors.paymentTermId = 'Payment Terms is required.';
      }
      if (!this.form.invoiceFrequency) {
        errors.invoiceFrequency = 'Invoice Frequency is required.';
      }
      
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async handleOk() {
      if (!this.validate()) {
        // Show error modal with validation errors
        const errorMessages = Object.values(this.errors)
          .map(message => `• ${message}`)
          .join('\n');
        this.errorMessage = errorMessages;
        this.showErrorModal = true;
        return;
      }
      
      this.abortPendingContactEmailChecks();
      this.syncContactEmailErrorState();

      // Check for contact email errors before saving (only non-empty emails; prune stale keys)
      const hasContactEmailErrors = Object.keys(this.contactEmailErrors).length > 0;
      if (hasContactEmailErrors) {
        const emailErrors = Object.values(this.contactEmailErrors);
        this.errorMessage = emailErrors.join('\n');
        this.showErrorModal = true;
        this.activeTab = 'contacts';
        return;
      }
      
      this.loading = true;
      try {
        // Get main address and address details
        const mainAddress = this.getMainAddress();
        
        const mergedContactsPayload = this.resolveContactsForSubmit();
        const contactsPayload =
          mergedContactsPayload.ok ? mergedContactsPayload.contacts : this.contacts;

        const payload = {
          ...this.form,
          salesPersonId: this.form.salesPerson || null,
          paymentTermId: this.form.paymentTermId || null,
          customerGroupId: this.form.customerGroup || null,
          invoiceFrequencyId: this.form.invoiceFrequency || null,
          contacts: contactsPayload,
          addresses: this.addresses,
          address: mainAddress.address,
          addressDetails: mainAddress.addressDetails,
          companyId: this.companyId,
          // Convert USA date format to ISO format
          customerSince: this.convertDateToISO(this.form.customerSince),
          balanceAsOf: this.convertDateToISO(this.form.balanceAsOf)
        };
        
        // Remove the form fields that are now mapped to different names
        delete payload.salesPerson;
        delete payload.customerGroup;
        delete payload.invoiceFrequency;
        
        const response = await customerService.createCustomer(payload);
        // Store the created customer data
        this.createdCustomer = response.data;
        
        // Upload documents if any
        if (this.documents.length > 0 && this.createdCustomer && this.createdCustomer.id) {
          await this.uploadDocuments(this.createdCustomer.id);
        }
        
        this.successMessage = 'Customer created successfully.\nStay on this page to view the details?';
        this.showSuccessModal = true;
      } catch (error) {
        let errorMessage = 'Error creating customer';
        
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Handle email-related errors
        if (errorMessage.includes('email') || errorMessage.includes('Email')) {
          if (errorMessage.includes('vendor') || errorMessage.includes('Vendor')) {
            this.errorMessage = 'This email address already exists for a vendor or vendor contact in this company.';
          } else if (errorMessage.includes('consultant')) {
            this.errorMessage = 'This email address already exists for a consultant in this company.';
          } else if (
            /customer\s+contact/i.test(errorMessage) ||
            /\bcontact\s+with\s+this\s+email\b/i.test(errorMessage) ||
            /already\s+exists\s+for\s+another\s+customer\s+in\s+this\s+company/i.test(errorMessage)
          ) {
            this.errorMessage = 'This email address already exists for another customer contact in this company.';
          } else {
            this.errorMessage = errorMessage;
          }
          this.showErrorModal = true;
          this.activeTab = 'contacts';
          return;
        }
        
        this.errorMessage = errorMessage;
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },

    // Contact Management Methods
    addNewContactRow() {
      const isFirst = this.contacts.length === 0 && this.newContacts.length === 0;
      this.newContacts.push({ name: '', title: '', phone: '', email: '', primary: isFirst });
      this.clearError('contacts');
    },

    async saveNewContact(idx) {
      const nc = this.newContacts[idx];
      if (!nc.name || !nc.title || !nc.phone || !nc.email) {
        this.errorMessage = 'Please fill in all contact fields before adding.';
        this.showErrorModal = true;
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(nc.email.trim())) {
        this.errorMessage = 'Please enter a valid email address.';
        this.showErrorModal = true;
        return;
      }
      
      // Validate email uniqueness (immediate check, no debounce)
      if (nc.email && nc.email.trim()) {
        await this.validateContactEmail(nc.email, idx, true, true);
        const emailErrorKey = `new-contact-${idx}`;
        if (this.contactEmailErrors[emailErrorKey]) {
          this.errorMessage = this.contactEmailErrors[emailErrorKey];
          this.showErrorModal = true;
          return;
        }
      }
      
      // If this is the first contact being added, ensure it's marked as primary
      if (this.contacts.length === 0) {
        nc.primary = true;
      } else if (nc.primary) {
        // If this new contact is marked as primary, uncheck all existing contacts
        this.contacts.forEach(c => c.primary = false);
      }
      
      this.contacts = [...this.contacts, nc];
      this.newContacts.splice(idx, 1);
      this.syncContactEmailErrorState();
      
      // Update the main form fields with the primary contact's info
      const primaryContact = this.contacts.find(c => c.primary);
      if (primaryContact) {
        this.form.phone = primaryContact.phone || '';
        this.form.email = primaryContact.email || '';
      }
      
      this.clearError('contacts');
    },

    cancelNewContact(idx) {
      this.newContacts.splice(idx, 1);
      this.syncContactEmailErrorState();
    },

    editContact(idx) {
      this.editingContactIdx = idx;
    },

    async updateContact() {
      const contact = this.contacts[this.editingContactIdx];
      
      // Validate required fields
      if (!contact.name || !contact.title || !contact.phone || !contact.email) {
        this.errorMessage = 'Please fill in all contact fields before updating.';
        this.showErrorModal = true;
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contact.email.trim())) {
        this.errorMessage = 'Please enter a valid email address.';
        this.showErrorModal = true;
        return;
      }
      
      // Validate email uniqueness (immediate check, no debounce)
      if (contact.email && contact.email.trim()) {
        await this.validateContactEmail(contact.email, this.editingContactIdx, false, true);
        const emailErrorKey = `contact-${this.editingContactIdx}`;
        if (this.contactEmailErrors[emailErrorKey]) {
          this.errorMessage = this.contactEmailErrors[emailErrorKey];
          this.showErrorModal = true;
          return;
        }
      }
      
      this.editingContactIdx = null;
      // Update the main form fields with the primary contact's info
      const primaryContact = this.contacts.find(c => c.primary);
      if (primaryContact) {
        this.form.phone = primaryContact.phone || '';
        this.form.email = primaryContact.email || '';
      }
    },

    cancelEditContact() {
      this.editingContactIdx = null;
    },

    deleteContact(idx) {
      this.contacts.splice(idx, 1);
      
      // If there's only one contact left, ensure it's marked as primary
      if (this.contacts.length === 1) {
        this.contacts[0].primary = true;
      }
      
      // If there are no contacts left, clear the main form fields
      if (this.contacts.length === 0) {
        this.form.phone = '';
        this.form.email = '';
      } else {
        // Update the main form fields with the current primary contact's info
        const primaryContact = this.contacts.find(c => c.primary);
        if (primaryContact) {
          this.form.phone = primaryContact.phone || '';
          this.form.email = primaryContact.email || '';
        }
      }
      
      this.syncContactEmailErrorState();
      this.clearError('contacts');
    },

    setPrimaryContact(idx) {
      // First, uncheck all other contacts
      this.contacts.forEach((c, i) => {
        if (i !== idx) {
          c.primary = false;
        }
      });
      
      // If this is the only contact, ensure it's marked as primary
      if (this.contacts.length === 1) {
        this.contacts[0].primary = true;
        this.clearError('contacts');
        return;
      }
      
      // If trying to uncheck the only primary contact, prevent it
      if (!this.contacts[idx].primary && this.contacts.filter(c => c.primary).length === 0) {
        this.contacts[idx].primary = true;
      }
      
      // Update the main form fields with the primary contact's info
      const primaryContact = this.contacts.find(c => c.primary);
      if (primaryContact) {
        this.form.phone = primaryContact.phone || '';
        this.form.email = primaryContact.email || '';
      }
      
      this.clearError('contacts');
    },

    // Address Management Methods
    addNewAddressRow() {
      this.newAddresses.push({ type: '', address1: '', address2: '', city: '', state: 'UT', postalCode: '', country: 'US' });
      this.clearError('addresses');
    },

    saveNewAddress(idx) {
      const na = this.newAddresses[idx];
      if (!na.type || !na.address1 || !na.city || !na.state || !na.postalCode || !na.country) {
        this.errorMessage = 'Please fill in all address fields before adding.';
        this.showErrorModal = true;
        return;
      }
      
      // Check for duplicate address type
      const allTypes = this.addresses.map(a => a.type).concat(this.newAddresses.map(a => a.type));
      if (allTypes.filter(t => t === na.type).length > 1) {
        this.errorMessage = 'This address type has already been added.';
        this.showErrorModal = true;
        return;
      }
      
      this.addresses = [...this.addresses, na];
      this.newAddresses.splice(idx, 1);
      this.clearError('addresses');
    },

    cancelNewAddress(idx) {
      this.newAddresses.splice(idx, 1);
    },

    editAddress(idx) {
      this.editingAddressIdx = idx;
    },

    updateAddress(idx) {
      const addr = this.addresses[idx];
      // Prevent duplicate address type (except for itself)
      const otherTypes = this.addresses.map((a, i) => i !== idx ? a.type : null).concat(this.newAddresses.map(a => a.type));
      if (otherTypes.includes(addr.type)) {
        this.errorMessage = 'This address type has already been added.';
        this.showErrorModal = true;
        return;
      }
      if (!addr.type || !addr.address1 || !addr.city || !addr.state || !addr.postalCode || !addr.country) {
        if (!addr.type) {
          this.errorMessage = 'Please select an address type before updating.';
        } else {
          this.errorMessage = 'Please fill in all address fields before updating.';
        }
        this.showErrorModal = true;
        return;
      }
      this.editingAddressIdx = null;
    },

    cancelEditAddress() {
      this.editingAddressIdx = null;
    },

    deleteAddress(idx) {
      this.addresses.splice(idx, 1);
      this.clearError('addresses');
    },

    getMainAddress() {
      // Priority: Billing address > first address > null
      const billingAddress = this.addresses.find(addr => addr.type === 'Billing');
      const mainAddr = billingAddress || this.addresses[0];
      
      if (!mainAddr) {
        return { address: null, addressDetails: null };
      }
      
      // Format address details into a single string
      const addressParts = [
        mainAddr.address1,
        mainAddr.address2,
        mainAddr.city,
        mainAddr.state,
        mainAddr.postalCode,
        mainAddr.country
      ].filter(part => part && part.trim() !== '');
      
      return {
        address: mainAddr.type,
        addressDetails: addressParts.join('\n')
      };
    },

    getAvailableAddressTypes(idx, isNew) {
      const allTypes = ["Billing", "Shipping", "Other"];
      // Collect used types from addresses (excluding the current row)
      let usedTypes = this.addresses.map((a, i) => (isNew ? a.type : (i !== idx ? a.type : null))).filter(Boolean);
      // Also exclude types being used in other newAddresses (for add rows)
      if (isNew) {
        usedTypes = usedTypes.concat(this.newAddresses.map((a, i) => (i !== idx ? a.type : null)).filter(Boolean));
      }
      // Always allow the current row's type (for edit rows)
      let currentType = isNew ? this.newAddresses[idx]?.type : this.addresses[idx]?.type;
      return allTypes.filter(type => !usedTypes.includes(type) || type === currentType);
    },
    // Data Fetching Methods
    async fetchPaymentTerms() {
      try {
        if (!this.companyId) return;
        const response = await ApiService.get(`/company/payment-terms?companyId=${this.companyId}`);
        this.paymentTerms = Array.isArray(response.data.paymentTerms) ? response.data.paymentTerms : [];
        // Payment terms fetched successfully
      } catch (error) {
        logger.error('Error fetching payment terms:', error);
        this.paymentTerms = [];
      }
    },

    async fetchSalesPersons() {
      try {
        if (!this.companyId) return;
        // Use the CompanyService method for consistency and correct endpoint
        this.salesPersons = await CompanyService.getAllSalesPersons(this.companyId);
      } catch (err) {
        this.salesPersons = [];
      }
    },

    async fetchCustomerGroups() {
      try {
        if (!this.companyId) return;
        const groups = await CompanyService.getAllCustomerGroups(this.companyId);
        this.customerGroups = Array.isArray(groups) ? groups : [];
      } catch (err) {
        this.customerGroups = [];
      }
    },

    async fetchInvoiceFrequencies() {
      try {
        if (!this.companyId) return;
        this.invoiceFrequencies = await CompanyService.getAllInvoiceFrequencies(this.companyId);
      } catch (err) {
        logger.error('Error fetching invoice frequencies:', err);
        this.invoiceFrequencies = [];
      }
    },

    // Dropdown Change Handlers
    handlePaymentTermChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new__') {
        this.showAddPaymentTermModal = true;
        // v-model already set to __add_new__; restore prior selection
        this.$nextTick(() => {
          this.form.paymentTermId = this.previousPaymentTerm || '';
        });
      } else {
        this.form.paymentTermId = selected;
        this.previousPaymentTerm = selected;
      }
    },

    handleCustomerGroupChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__create_new__') {
        this.showAddCustomerGroupModal = true;
        // Reset the select to previous value after showing modal
        this.$nextTick(() => {
          this.form.customerGroup = this.previousCustomerGroup;
        });
      } else {
        this.form.customerGroup = selected;
        this.previousCustomerGroup = selected;
      }
    },

    handleSalesPersonChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__create_new__') {
        this.showAddSalesPersonModal = true;
        // Reset the select to previous value after showing modal
        this.$nextTick(() => {
          this.form.salesPerson = this.previousSalesPerson;
        });
      } else {
        this.form.salesPerson = selected;
        this.previousSalesPerson = selected;
      }
    },

    handleInvoiceFrequencyChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__create_new__') {
        this.showAddInvoiceFrequencyModal = true;
        // Reset the select to previous value after showing modal
        this.$nextTick(() => {
          this.form.invoiceFrequency = this.previousInvoiceFrequency;
        });
      } else {
        this.form.invoiceFrequency = selected;
        this.previousInvoiceFrequency = selected;
        this.clearError('invoiceFrequency');
      }
    },

    // Modal Handlers with Enhanced Payment Terms Fix
    async onAddPaymentTermOk(paymentTerm) {
      this.showAddPaymentTermModal = false;
      
      try {
        // Refresh payment terms list
      await this.fetchPaymentTerms();
      
      // Auto-select the newly created payment term
        if (paymentTerm) {
          // Handle different possible data structures
          const termId = paymentTerm.id || paymentTerm.paymentTermId || paymentTerm;
          
          if (termId) {
            // Use $nextTick to ensure the DOM is updated with new options
            this.$nextTick(() => {
              this.form.paymentTermId = String(termId);
              this.previousPaymentTerm = String(termId);
              
              // Verify the term was actually added to the list
              const termExists = this.paymentTerms.find(term => 
                String(term.id) === String(termId)
              );
              
              if (!termExists) {
                logger.warn('Newly created payment term not found in list, refetching...');
                // If term not found, try fetching again after a short delay
                setTimeout(async () => {
                  await this.fetchPaymentTerms();
                  this.$nextTick(() => {
                    this.form.paymentTermId = String(termId);
                    this.previousPaymentTerm = String(termId);
                  });
                }, 500);
              }
            });
            
            // Show success message if toast is available
            if (this.$toast) {
              this.$toast.success('Payment term created and selected successfully!');
            }
          } else {
            logger.warn('Payment term created but ID not found in response:', paymentTerm);
            if (this.$toast) {
              this.$toast.success('Payment term created successfully!');
            }
          }
        }
      } catch (error) {
        logger.error('Error handling new payment term:', error);
        if (this.$toast) {
          this.$toast.error('Payment term created but there was an issue selecting it. Please select it manually.');
        }
      }
    },

    onAddPaymentTermCancel() {
      this.showAddPaymentTermModal = false;
      // Reset to previous value when cancelled
      this.form.paymentTermId = this.previousPaymentTerm;
    },

    async onAddCustomerGroupOk(response) {
      this.showAddCustomerGroupModal = false;
      await this.fetchCustomerGroups();
      
      // Auto-select the newly created customer group
      // The backend returns { message: 'Customer group created successfully', group }
      if (response && response.group && response.group.id) {
        this.form.customerGroup = response.group.id;
        this.previousCustomerGroup = response.group.id;
        this.$toast && this.$toast.success('Customer group created successfully!');
      }
    },

    onAddCustomerGroupCancel() {
      this.showAddCustomerGroupModal = false;
      // Reset to previous value when cancelled
      this.form.customerGroup = this.previousCustomerGroup;
    },

    async onAddSalesPersonOk(salesPerson) {
      this.showAddSalesPersonModal = false;
      await this.fetchSalesPersons();
      
      // Auto-select the newly created sales person
      // Handle different possible response structures
      let salesPersonId = null;
      
      if (salesPerson) {
        // Direct ID
        if (salesPerson.id) {
          salesPersonId = salesPerson.id;
        }
        // Nested in data property
        else if (salesPerson.data && salesPerson.data.id) {
          salesPersonId = salesPerson.data.id;
        }
        // Nested in salesPerson property
        else if (salesPerson.salesPerson && salesPerson.salesPerson.id) {
          salesPersonId = salesPerson.salesPerson.id;
        }
      }
      
      if (salesPersonId) {
        this.form.salesPerson = salesPersonId;
        this.previousSalesPerson = salesPersonId;
        // Show success message if toast is available
        if (this.$toast) {
          this.$toast.success('Sales person created successfully!');
        }
      } else {
        if (this.$toast) {
          this.$toast.info('Sales person created successfully! Please select it from the dropdown.');
        }
      }
    },

    onAddSalesPersonCancel() {
      this.showAddSalesPersonModal = false;
      // Reset to previous value when cancelled
      this.form.salesPerson = this.previousSalesPerson;
    },

    async onAddInvoiceFrequencyOk(invoiceFrequency) {
      this.showAddInvoiceFrequencyModal = false;
      await this.fetchInvoiceFrequencies();
      
      // Auto-select the newly created invoice frequency
      if (invoiceFrequency && invoiceFrequency.id) {
        this.form.invoiceFrequency = invoiceFrequency.id;
        this.previousInvoiceFrequency = invoiceFrequency.id;
        this.$toast && this.$toast.success('Invoice frequency created successfully!');
      }
    },

    onAddInvoiceFrequencyCancel() {
      this.showAddInvoiceFrequencyModal = false;
      // Reset to previous value when cancelled
      this.form.invoiceFrequency = this.previousInvoiceFrequency;
    },

    handleCancel() {
      this.resetForm();
      this.$emit('cancel');
    },

    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },

    abortPendingContactEmailChecks() {
      Object.keys(this.contactEmailCheckTimeouts || {}).forEach((k) => {
        clearTimeout(this.contactEmailCheckTimeouts[k]);
        delete this.contactEmailCheckTimeouts[k];
      });
      Object.keys(this.checkingContactEmails || {}).forEach((k) => {
        this.checkingContactEmails[k] = false;
      });
    },

    getContactEmailSnapshot(idx, isNew) {
      const row = isNew ? this.newContacts[idx] : this.contacts[idx];
      return row ? String(row.email || '').trim() : '';
    },
    
    handleContactEmailInput(event, idx, isNew) {
      const email = typeof event === 'string' ? event : (event?.target?.value ?? '');
      const key = isNew ? `new-contact-${idx}` : `contact-${idx}`;
      this.clearContactEmailError(key);
      
      if (!email || !email.trim()) {
        return;
      }
      
      // Clear previous timeout
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
      }
      
      // Debounce the validation (read live value — stale closure caused wrong errors after edits)
      this.contactEmailCheckTimeouts[key] = setTimeout(() => {
        const live = this.getContactEmailSnapshot(idx, isNew);
        this.validateContactEmail(live || '', idx, isNew);
      }, 500);
    },
    
    syncContactEmailErrorState() {
      [...Object.keys(this.contactEmailErrors)].forEach((rawKey) => {
        const contactMatch = /^contact-(\d+)$/.exec(rawKey);
        const newContactMatch = /^new-contact-(\d+)$/.exec(rawKey);
        if (contactMatch) {
          const i = Number(contactMatch[1]);
          if (i >= this.contacts.length) {
            this.clearContactEmailError(rawKey);
            return;
          }
          const row = this.contacts[i];
          if (!row || !String(row.email || '').trim()) {
            this.clearContactEmailError(rawKey);
          }
          return;
        }
        if (newContactMatch) {
          const i = Number(newContactMatch[1]);
          if (i >= this.newContacts.length) {
            this.clearContactEmailError(rawKey);
            return;
          }
          const row = this.newContacts[i];
          if (!row || !String(row.email || '').trim()) {
            this.clearContactEmailError(rawKey);
          }
          return;
        }
        this.clearContactEmailError(rawKey);
      });
    },

    async validateContactEmail(email, idx, isNew, immediate = false) {
      const key = isNew ? `new-contact-${idx}` : `contact-${idx}`;
      if (!email || !email.trim()) {
        this.clearContactEmailError(key);
        return;
      }
      
      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        this.contactEmailErrors[key] = 'Please enter a valid email address.';
        this.checkingContactEmails[key] = false;
        return;
      }
      
      if (!this.companyId) {
        this.checkingContactEmails[key] = false;
        return;
      }
      
      // Clear previous timeout
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
      }
      
      // If immediate, check right away; otherwise debounce
      const performCheck = async () => {
        if (this.getContactEmailSnapshot(idx, isNew) !== String(email || '').trim()) {
          return;
        }
        this.checkingContactEmails[key] = true;
        delete this.contactEmailErrors[key];
        
        try {
          const response = await customerService.checkContactEmailExists(email.trim(), this.companyId, null);
          
          const result = response.data || {};
          if (result.exists) {
            let errorMessage = '';
            const asVendor = Boolean(result.existsAsVendor);
            const asConsultant = Boolean(result.existsAsConsultant);
            const asCustomerEmail = Boolean(result.existsAsCustomerEmail);
            const asContact = Boolean(result.existsAsContact);
            if (asVendor) {
              errorMessage = 'This email address already exists for a vendor or vendor contact in this company.';
            } else if (asConsultant) {
              errorMessage = 'This email address already exists for a consultant in this company.';
            } else if (asCustomerEmail) {
              errorMessage =
                'This email address is already used as another customer\'s email in this company. Use the customer profile or remove the duplicate first.';
            } else if (asContact) {
              errorMessage = 'This email address already exists for another customer contact in this company.';
            } else {
              errorMessage =
                'This email address is already in use in this company. It may appear on another customer profile, contact, consultant, or vendor.';
            }
            if (this.getContactEmailSnapshot(idx, isNew) === email.trim()) {
              this.contactEmailErrors[key] = errorMessage;
            }
          }
        } catch (error) {
          logger.error('Error checking contact email:', error);
          // Don't show error to user for validation check failures
        } finally {
          this.checkingContactEmails[key] = false;
        }
      };
      
      if (immediate) {
        await performCheck();
      } else {
        this.contactEmailCheckTimeouts[key] = setTimeout(performCheck, 500);
      }
    },
    
    clearContactEmailError(key) {
      delete this.contactEmailErrors[key];
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
        delete this.contactEmailCheckTimeouts[key];
      }
      this.checkingContactEmails[key] = false;
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successMessage = '';
    },
    stayOnPageAfterSave() {
      const created = this.createdCustomer;
      this.closeSuccessModal();
      if (created && created.id && this.companyId) {
        this.$emit('ok', created);
        this.$emit('close');
        this.$router.push(`/company/${this.companyId}/dashboard/customers/edit/${created.id}`);
        return;
      }
    },
    leavePageAfterSave() {
      const created = this.createdCustomer;
      this.closeSuccessModal();
      this.resetForm();
      this.$emit('ok', created);
      this.$emit('close');
    },
    // Document handling methods
    handleDocumentFileUpload(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const fileArray = Array.from(files);
      let processedCount = 0;
      const totalFiles = fileArray.length;

      fileArray.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const getMimeType = (filename, fallbackType) => {
            if (fallbackType && fallbackType !== '') return fallbackType;
            const extension = filename.split('.').pop().toLowerCase();
            const mimeMap = {
              'pdf': 'application/pdf',
              'doc': 'application/msword',
              'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'xls': 'application/vnd.ms-excel',
              'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'jpg': 'image/jpeg',
              'jpeg': 'image/jpeg',
              'png': 'image/png',
              'gif': 'image/gif',
              'txt': 'text/plain'
            };
            return mimeMap[extension] || 'application/octet-stream';
          };

          const document = {
            fileName: file.name,
            fileSize: file.size,
            filePath: e.target.result,
            mimeType: getMimeType(file.name, file.type),
            uploadedAt: new Date().toISOString(),
            file: file // Store the actual file for upload
          };
          this.documents.push(document);
          processedCount++;
          
          // Auto-select the last document when all files are processed
          if (processedCount === totalFiles) {
            this.$nextTick(async () => {
              this.selectedDocumentIndex = this.documents.length - 1;
              await this.selectDocument(this.selectedDocumentIndex);
            });
          } else if (this.selectedDocumentIndex === null && processedCount === 1) {
            // Select first document if none selected
            this.$nextTick(async () => {
              this.selectedDocumentIndex = 0;
              await this.selectDocument(0);
            });
          }
        };
        reader.readAsDataURL(file);
      });

      if (this.$refs.addDocumentFileInput) {
        this.$refs.addDocumentFileInput.value = '';
      }
    },
    removeCustomerDocument(index) {
      if (this.documents && this.documents.length > index) {
        // Remove preview URL from cache and revoke blob URL if needed
        const document = this.documents[index];
        if (document && document.filePath) {
          const cachedUrl = this.documentPreviewUrls[document.filePath];
          if (cachedUrl && cachedUrl.startsWith('blob:')) {
            window.URL.revokeObjectURL(cachedUrl);
          }
          delete this.documentPreviewUrls[document.filePath];
        }
        
        // If removing the selected document, clear selection and preview
        if (this.selectedDocumentIndex === index) {
          this.selectedDocumentIndex = null;
          this.currentPreviewUrl = '';
          this.previewError = false;
        } else if (this.selectedDocumentIndex > index) {
          // Adjust selected index if document before it was removed
          this.selectedDocumentIndex--;
        }
        
        this.documents.splice(index, 1);
      }
    },
    async selectDocument(index) {
      this.selectedDocumentIndex = index;
      this.previewError = false;
      this.currentPreviewUrl = '';
      
      const document = this.documents[index];
      if (!document) return;
      
      // Check if URL is already cached
      if (this.documentPreviewUrls[document.filePath]) {
        this.currentPreviewUrl = this.documentPreviewUrls[document.filePath];
        return;
      }
      
      // Load preview URL
      this.loadingPreview = true;
      try {
        const url = await this.getDocumentPreviewUrl(document);
        if (url) {
          this.currentPreviewUrl = url;
        } else {
          this.previewError = true;
        }
      } catch (error) {
        logger.error('Error loading preview:', error);
        this.previewError = true;
      } finally {
        this.loadingPreview = false;
      }
    },
    handlePreviewError() {
      this.previewError = true;
      this.currentPreviewUrl = '';
    },
    async getDocumentPreviewUrl(document) {
      if (!document || !document.filePath) return '';
      
      // If already cached, return cached URL
      if (this.documentPreviewUrls[document.filePath]) {
        return this.documentPreviewUrls[document.filePath];
      }
      
      // If it's a new document (data URL), use it directly
      if (document.filePath.startsWith('data:')) {
        this.documentPreviewUrls[document.filePath] = document.filePath;
        return document.filePath;
      }
      
      // For saved documents, fetch from backend
      if (document.id) {
        try {
          if (!this.companyId) return '';
          
          const response = await customerService.viewCustomerDocument(document.id, this.companyId);
          const blob = new Blob([response.data], { type: document.mimeType || 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          this.documentPreviewUrls[document.filePath] = url;
          return url;
        } catch (error) {
          logger.error('Failed to load document preview:', error);
          return '';
        }
      }
      
      return '';
    },
    isImageFile(document) {
      if (!document || !document.mimeType) return false;
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      return imageTypes.includes(document.mimeType.toLowerCase());
    },
    isPdfFile(document) {
      if (!document || !document.mimeType) return false;
      return document.mimeType.toLowerCase() === 'application/pdf';
    },
    async openDocumentInNewWindow(document) {
      const url = await this.getDocumentPreviewUrl(document);
      if (url) {
        window.open(url, '_blank');
      } else {
        this.errorMessage = 'Failed to open document';
        this.showErrorModal = true;
      }
    },
    async uploadDocuments(customerId) {
      if (!this.documents || this.documents.length === 0) return;
      
      if (!this.companyId) {
        logger.error('Company ID not found');
        return;
      }
      
      // Filter documents that haven't been uploaded yet (have file property)
      const documentsToUpload = this.documents.filter(doc => doc.file);
      
      if (documentsToUpload.length === 0) return;
      
      try {
        const formData = new FormData();
        formData.append('customerId', customerId);
        formData.append('companyId', this.companyId);
        
        documentsToUpload.forEach((doc) => {
          if (doc.file) {
            formData.append('documents', doc.file);
          }
        });
        
        const response = await customerService.uploadCustomerDocuments(customerId, this.companyId, formData);
        logger.log('Documents uploaded successfully', response.data);
        
        // Update documents array with uploaded document IDs
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          const uploadedDocs = response.data.data;
          documentsToUpload.forEach((doc, index) => {
            if (uploadedDocs[index]) {
              const uploadedDoc = uploadedDocs[index];
              // Find the document in the array and update it
              const docIndex = this.documents.findIndex(d => d === doc);
              if (docIndex !== -1) {
                this.documents[docIndex].id = uploadedDoc.id;
                this.documents[docIndex].customerId = uploadedDoc.customerId;
                this.documents[docIndex].uploadedAt = uploadedDoc.uploadedAt;
                // Keep filePath as data URL for preview, but also store the filePath from server
                this.documents[docIndex].serverFilePath = uploadedDoc.filePath;
                
                // If this is the currently selected document, reload preview
                if (this.selectedDocumentIndex === docIndex) {
                  this.$nextTick(() => {
                    this.selectDocument(docIndex);
                  });
                }
              }
            }
          });
        }
      } catch (error) {
        logger.error('Error uploading documents:', error);
        // Don't throw - allow customer creation to succeed even if document upload fails
        this.errorMessage = 'Customer created but some documents failed to upload: ' + (error.message || 'Please try uploading documents again');
        this.showErrorModal = true;
      }
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    }
  },
  beforeUnmount() {
    // Cleanup blob URLs to prevent memory leaks
    Object.values(this.documentPreviewUrls).forEach(url => {
      if (url && url.startsWith('blob:')) {
        window.URL.revokeObjectURL(url);
      }
    });
  },

  computed: {
    paymentMethodDropdownOptions() {
      const methods = [
        'Cash',
        'Check',
        'Credit Card',
        'Direct Deposit',
        'Master card',
        'Online Banking',
        'Standing Order',
        'Switch/Maestro',
      ];
      return [
        { value: '', label: 'Select' },
        ...methods.map((method) => ({ value: method, label: method })),
      ];
    },
    salesPersonDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.salesPersons || []).map((person) => ({
          value: person.id,
          label: person.salesPersonName,
        })),
        { value: '__create_new__', label: 'Add New Sales Person' },
      ];
    },
    paymentTermsDropdownOptions() {
      return [
        { value: '', label: 'Select Payment Term' },
        ...(this.paymentTerms || []).map((term) => ({
          value: term.id,
          label: `${term.name} (${term.description})`,
        })),
        { value: '__add_new__', label: 'Add New Payment Term' },
      ];
    },
    customerGroupDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.customerGroups || []).map((group) => ({
          value: group.id,
          label: group.name,
        })),
        { value: '__create_new__', label: 'Add New Group' },
      ];
    },
    invoiceFrequencyDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.invoiceFrequencies || []).map((frequency) => ({
          value: frequency.id,
          label: frequency.name,
        })),
        { value: '__create_new__', label: 'Add New Invoice Frequency' },
      ];
    },
    hasCurrentTabErrors() {
      // Check if current tab has validation errors
      if (this.activeTab === 'general') {
        return (!this.form.customerId || this.form.customerId.trim() === '') ||
               this.customerIdExists ||
               (!this.form.customerName || this.form.customerName.trim() === '') ||
               (this.form.customerSince && !this.validateDate('customerSince')) ||
               (this.form.balanceAsOf && !this.validateDate('balanceAsOf'));
      }
      
      if (this.activeTab === 'details') {
        return !this.form.paymentMethod || !this.form.paymentTermId || !this.form.invoiceFrequency;
      }
      
      if (this.activeTab === 'contacts') {
        return !this.resolveContactsForSubmit().ok;
      }
      
      if (this.activeTab === 'addresses') {
        return false;
      }
      
      // Documents tab has no validation errors (documents are optional)
      if (this.activeTab === 'documents') {
        return false;
      }
      
      return false;
    }
  },

  mounted() {
    // If component is mounted with visible=true and companyId, fetch data
    if (this.visible && this.companyId) {
      this.fetchPaymentTerms();
      this.fetchSalesPersons();
      this.fetchCustomerGroups();
      this.fetchInvoiceFrequencies();
    }
    // If component is mounted with initialCustomerName and visible, set the data
    if (this.visible && this.initialCustomerName && this.initialCustomerName.trim()) {
      this.setInitialCustomerData();
    }
  },

  watch: {
    visible(newVal) {
      if (!newVal) {
        // After a successful save, keep SuccessModal mounted until the user dismisses it
        if (!this.showSuccessModal) {
          this.resetForm();
        }
      } else {
        // Load data when dialog opens (only if companyId is available)
        if (this.companyId) {
          this.fetchPaymentTerms();
          this.fetchSalesPersons();
          this.fetchCustomerGroups();
          this.fetchInvoiceFrequencies();
        }
        
        // Pre-fill customer name and auto-generate customer ID if provided
        // Use a small delay to ensure form is ready after resetForm
        this.$nextTick(() => {
          this.setInitialCustomerData();
        });
      }
    },
    
    companyId(newVal, oldVal) {
      // When companyId changes and dialog is visible, fetch data
      if (newVal && this.visible && newVal !== oldVal) {
        this.fetchPaymentTerms();
        this.fetchSalesPersons();
        this.fetchCustomerGroups();
        this.fetchInvoiceFrequencies();
      }
    },
    
    initialCustomerName(newVal) {
      // Watch for changes to initialCustomerName prop when dialog is visible
      if (newVal && newVal.trim() && this.visible) {
        this.$nextTick(() => {
          this.setInitialCustomerData();
        });
      }
    },
    
    initialCustomerId(newVal) {
      // Watch for changes to initialCustomerId prop when dialog is visible
      if (newVal && newVal.trim() && this.visible && this.initialCustomerName) {
        this.$nextTick(() => {
          this.setInitialCustomerData();
        });
      }
    },

    'form.openingBalance': function(val) {
      // Only set balance if creating a new customer (not editing)
      this.form.balance = val;
    },

    contacts: {
      handler(newContacts) {
        // If only one contact, make it primary
        if (newContacts.length === 1) {
          newContacts[0].primary = true;
        }
        
        // Ensure at least one contact is primary
        const primary = newContacts.find(c => c.primary);
        if (primary) {
          this.form.phone = primary.phone || '';
          this.form.email = primary.email || '';
        } else if (newContacts.length > 0) {
          // If there are contacts but none are primary, mark the first one as primary
          newContacts[0].primary = true;
          this.form.phone = newContacts[0].phone || '';
          this.form.email = newContacts[0].email || '';
        } else {
          // No contacts, clear the main form fields
          this.form.phone = '';
          this.form.email = '';
        }
      },
      deep: true
    }
  }
};
</script>

